/**
 * WorkspaceMarker - single source of truth for the tiny visual a workspace
 * row carries everywhere in the UI (sidebar tree, switcher chip, drafting-for
 * pill, dropdown trigger). Renders, in priority order:
 *
 *   1. `icon_url` (square preview) when set, resolved via `workspaceAssetUrl`.
 *   2. `logo_url` (wide preview) when no icon is set.
 *   3. A solid color dot using `workspace.color`, falling back to the
 *      product accent purple when no color is stored either.
 *
 * Colocated with the other workspace primitives so future consumers don't
 * each reinvent the same fallback chain. Mirrors the contract previously
 * inlined as `DraftingForMarker` in `CreateInterviewForm.tsx` and
 * `WorkspaceMarker` in `SidebarWorkspaceTree.tsx` / `WorkspaceSwitcher.tsx`.
 *
 * @module components/workspace/WorkspaceMarker
 */

import React from 'react';
import type { Workspace } from '../../lib/org-types';
import { workspaceAssetUrl } from './assetUrl';

/** Default accent color when a workspace has no `color` set. */
export const DEFAULT_WORKSPACE_COLOR = '#7c3aed';

/** Variants for the marker. `square` gets rounded corners; `dot` gets a circle. */
export type WorkspaceMarkerShape = 'square' | 'dot';

/** Props accepted by `WorkspaceMarker`. */
export interface WorkspaceMarkerProps {
  /** Workspace whose marker we're rendering. Only `id`, `logo_url`, `icon_url`, and `color` are read. */
  workspace: Pick<Workspace, 'id' | 'logo_url' | 'icon_url' | 'color'>;
  /** Edge length in pixels. Default `24` (matches the chat-row + chip uses). */
  size?: number;
  /** Whether the dot fallback is rounded as a circle (`dot`) or as a square (`square`). */
  shape?: WorkspaceMarkerShape;
  /** Optional `data-testid` so test sites can locate the marker without coupling to the variant. */
  testId?: string;
  /** Extra Tailwind classes appended to the rendered element. */
  className?: string;
}

/**
 * Render the workspace marker. Prefers icon_url, then logo_url, then a
 * color dot. The image variant uses `object-cover` so the asset always
 * fills its box without distortion.
 */
export const WorkspaceMarker: React.FC<WorkspaceMarkerProps> = ({
  workspace,
  size = 24,
  shape = 'square',
  testId,
  className,
}) => {
  const src =
    workspaceAssetUrl(workspace, 'icon') ??
    workspaceAssetUrl(workspace, 'logo');
  const dimension = `${size}px`;

  if (src) {
    return (
      <img
        data-testid={testId}
        src={src}
        alt=""
        aria-hidden="true"
        className={`rounded-lg object-cover shrink-0 ${className ?? ''}`.trim()}
        style={{ width: dimension, height: dimension }}
      />
    );
  }

  const dotShape = shape === 'dot' ? 'rounded-full' : 'rounded-lg';
  return (
    <span
      data-testid={testId}
      aria-hidden="true"
      className={`inline-block shrink-0 ${dotShape} ${className ?? ''}`.trim()}
      style={{
        width: dimension,
        height: dimension,
        backgroundColor: workspace.color || DEFAULT_WORKSPACE_COLOR,
      }}
    />
  );
};

export default WorkspaceMarker;
